import { FC, PropsWithChildren } from 'react'
import {
    Navigate,
    useLocation,
    useParams,
    useSearchParams,
} from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { useSelector } from 'react-redux'
import { getProjectIdQuery } from '@Admin/AdminQuotingPage/query'
import { MFA_ENFORCE_QUERY_STRING } from '@User/MyAccountPage/AccountSettings/components/MfaSection'

import { getUserIdentity } from '~/redux-modules/identity'
import {
    ADMIN_PATH,
    LOGIN_PATH,
    QUOTES_PATH,
    ADMIN_403_PATH,
} from '~/redux-modules/preferences'
import isFlagEnabled from '~/lib/isFlagEnabled'
import {
    useGetQuoteIdFromQmrIdQuery,
    useGetProjectIdFromQuoteItemQuery,
} from '~/gql/graphql'
import SuspenseLoader from '~/core/SuspenseLoader'
import { PrivateRouteProps } from '~/apps/routes/RoutesTypes'
import { PROVISIONED_UI_FLAGS } from '~/apps/consumer/pages/Quotes/Admin/constants'

const MY_ACCOUNT_PAGE_PATH = '/pages/my-account'
const DEFAULT_PATH = ['/', QUOTES_PATH, ADMIN_PATH]

export const PrivateRoute: FC<PropsWithChildren<PrivateRouteProps>> = ({
    children,
    verifyAdmin,
    isQmrPage,
    getQuoteId,
}) => {
    const location = useLocation()
    const params = useParams()
    const [searchParams] = useSearchParams()
    const identity = useSelector(getUserIdentity)
    const quoteItemId = searchParams.get('quoteItemId')

    const qmrId = isQmrPage ? params?.id : undefined
    const projectIdFromUrl = params?.projectId
    const path = location.pathname
    // let projectIdQuery, queryId,
    let hasQMRAccess, projectId

    const isPartReusabilityEnabled = isFlagEnabled(
        identity,
        PROVISIONED_UI_FLAGS.ENABLE_PART_REUSABILITY,
        'true',
    )

    const {
        data: quoteModelRevisionData,
        loading: quoteModelRevisionDataQueryLoading,
    } = useQuery(getProjectIdQuery, {
        variables: {
            id: qmrId,
            showDeleted: identity.isAdmin,
            fetchPolicy: 'no-cache',
        },
        skip: !isQmrPage || !qmrId,
    })

    const { data: quoteItemData, loading: quoteItemDataQueryLoading } =
        useGetProjectIdFromQuoteItemQuery({
            variables: { id: quoteItemId, showDeleted: identity.isAdmin },
            skip: !isQmrPage || !isPartReusabilityEnabled,
        })

    const { data: qmrData, loading: qmrLoading } = useGetQuoteIdFromQmrIdQuery({
        variables: {
            id: params?.revisionId,
        },
        skip: !params?.revisionId || Boolean(params?.quoteId),
    })

    if (isPartReusabilityEnabled) {
        hasQMRAccess = isQmrPage
            ? Boolean(quoteItemData?.quoteItem?.quoteModelRevision)
            : false
        projectId = quoteItemData?.quoteItem?.quote?.project?.id
    } else {
        // if not admin or no access, quoteModelRevision will be null
        hasQMRAccess = isQmrPage
            ? Boolean(quoteModelRevisionData?.quoteModelRevision)
            : false

        //  if admin has access to QMR within project
        projectId =
            quoteModelRevisionData?.quoteModelRevision?.quoteModel?.quote
                ?.project?.id
    }

    if (
        identity.loading ||
        quoteModelRevisionDataQueryLoading ||
        qmrLoading ||
        quoteItemDataQueryLoading
    ) {
        return <SuspenseLoader />
    }
    const enforce2faEnabled = isFlagEnabled(identity, 'ENFORCE_2FA', 'true')

    if (
        // force admin on MyAccountPage if they didn't enable 2fa after enforce flag on
        identity.isAdmin &&
        enforce2faEnabled &&
        identity.mfaEnabled === false &&
        path !== MY_ACCOUNT_PAGE_PATH
    ) {
        return (
            <Navigate
                to={`${MY_ACCOUNT_PAGE_PATH}?${MFA_ENFORCE_QUERY_STRING}`}
            />
        )
    }

    if (identity.id === null) {
        const shouldSetReturnTo = !DEFAULT_PATH.includes(location.pathname)
        const returnToPath = `return_to=${location.pathname}`
        const loginPath = `${LOGIN_PATH}${location?.search || ''}${
            location?.search && shouldSetReturnTo ? '&' : '?'
        }${shouldSetReturnTo ? returnToPath : ''}`

        return <Navigate to={loginPath} />
    }

    // If route requires an admin but you're not one,
    // then redirect to /
    if (verifyAdmin && !identity.isAdmin) {
        if (path === '/') {
            // in the unlikely event that / is admin only,
            // to prevent infinite loop issue, we just render
            // an infinite loading screen instead.
            return <SuspenseLoader />
        }
        return <Navigate to={'/'} />
    }

    // If admin does not have access to see qmr associated with a Project, redirect to 403.
    if (isQmrPage && !hasQMRAccess) {
        return <Navigate to={`${ADMIN_PATH}${ADMIN_403_PATH}`} />
    }

    // This redirect will be done as a part of DFM expansion.
    // Once enabled the flag will disbale the old builder 3D viewer and redirect to configuration modal tabs
    if (getQuoteId) {
        let quoteId

        if (isPartReusabilityEnabled) {
            quoteId = quoteItemData?.quoteItem?.quote?.id
        } else {
            quoteId = qmrData?.quoteModelRevision?.quoteModel?.quote?.id
        }
        //checking for OBO vs Admin's own quote by checking whether userId and quoteId are defined in params
        const isInQuoteObo =
            identity.isAdmin &&
            params.userId &&
            params.userId !== identity.id &&
            params.quoteId
        let toPath

        if (params?.tabs || params?.sidebarId) {
            toPath = isInQuoteObo
                ? `/admin/app/user/${params.userId}/quotes/${params.quoteId}`
                : `/pages/quotes/${quoteId}/part/${params.revisionId}/${
                      params?.tabs || params?.sidebarId || ''
                  }`
        } else {
            toPath = isInQuoteObo
                ? `/admin/app/user/${params.userId}/quotes/${params.quoteId}`
                : `/pages/quotes/${quoteId}/part/${params.revisionId}`
        }

        return <Navigate to={toPath} />
    }

    // If admin associated with a Project, redirect with projectId
    if (isQmrPage && projectId && !projectIdFromUrl) {
        return <Navigate to={`${location.pathname}/projects/${projectId}`} />
    }
    // Finally, the user is logged in and, if required, they
    // are an admin, so we can render the route.
    return <>{children}</>
}
